<template>
  <div class="limits-carousel" v-show="hasLimits">
    <v-card class="elevation-0 limits-carousel__subtitle">
      {{ $t('business_limits') }}
      <Tooltip
        :text="$t('business_limits_tooltip')"
        :link="limitsLink"
        position="top"
        :delay="1000"
      />
    </v-card>

    <v-btn
      v-if="isSliderNeeded"
      class="limits-carousel__controls"
      elevation="0"
      color="surface"
      @click="prev"
    >
      <v-icon>chevron_left</v-icon>
    </v-btn>

    <span v-if="isSliderNeeded" class="limits-carousel__slider">
      <VueTinySlider
        ref="tinySlider"
        :options="options"
      >
        <div
          v-for="limit in limits"
          :key="limit.title"
        >
          <LimitsItem
            :title="limit.title"
            :limits="limit"
          />
        </div>
      </VueTinySlider>
    </span>

    <div
      v-else
      class="limits-carousel__no-scroll"
    >
      <div
        v-for="limit in limits"
        :key="limit.title"
      >
        <LimitsItem
          :title="limit.title"
          :limits="limit"
        />
      </div>
    </div>

    <v-btn
      v-if="isSliderNeeded"
      class="limits-carousel__controls"
      elevation="0"
      color="surface"
      @click="next"
    >
      <v-icon>chevron_right</v-icon>
    </v-btn>
  </div>
</template>

<script>
  import VueTinySlider from "@mentorkadriu/vue-tiny-slider";
  import Tooltip from './tooltips/Tooltip';
  import LimitsItem from "@/components/LimitsItem.vue";

  export default {
    components: {
      Tooltip,
      LimitsItem,
      VueTinySlider
    },
    mounted() {
      this.$nextTick(() => {
        window.addEventListener('resize', () => {
          this.windowWidth = window.innerWidth
        })
      })
    },
    data() {
      return {
        progress: 65,
        limitsLink: import.meta.env.VUE_APP_HELP_LIMITS_URL,
        windowWidth: window.innerWidth,
        options: {
          mouseDrag: true,
          loop: true,
          nav: false,
          slideBy: 'page',
          controls: false,
          arrowKeys: false,
          swipeAngle: false,
          items: 1,
          gutter: 8,
          edgePadding: 0,
          responsive: {
            600: {
              items: 2,
              edgePadding: 50
            },
            1280: {
              items: 3
            },
            1660: {
              items: 4
            }
          }
        }
      };
    },
    computed: {
      // Read from the slider's own breakpoints, so the two can't disagree.
      itemsPerView() {
        const { items, responsive } = this.options;
        return Object.entries(responsive)
          .filter(([minWidth]) => this.windowWidth >= Number(minWidth))
          .reduce((count, [, breakpoint]) => breakpoint.items ?? count, items);
      },
      isSliderNeeded() {
        return this.limits?.length > this.itemsPerView;
      },
      limits() {
        return this.$store.getters['businessUsers/limits'];
      },
      hasLimits() {
        return this.limits.length > 0;
      },
    },
    created() {
      this.getLimits();
    },
    methods: {
      next() {
        this.$refs.tinySlider.getSlider().goTo('next');
      },
      prev() {
        this.$refs.tinySlider.getSlider().goTo('prev');
      },
      getLimits() {
        return this.$store.dispatch('businessUsers/getLimits');
      },
      onResize() {
        this.windowWidth = window.innerWidth
      }
    }
  };
</script>

<style lang="scss">
@use 'sass:map';
  @use '../assets/styles/theme' as *;
  @use './tiny-slider/src/tiny-slider' as *;

  .limits-carousel {
    display: flex;
    justify-content: space-between;

    // 180px is the Business limits label, 96px the two controls buttons.
    &__slider {
      width: calc(100% - 96px - 180px);
    }

    &__no-scroll {
      display: flex;
      justify-content: flex-start;
      gap: 8px;
      width: calc(100% - 180px);
    }

    @include phone {
      flex-wrap: wrap;
      row-gap: 8px;

      .limits-carousel__subtitle {
        justify-content: flex-start;
        width: 100%;
      }

      .limits-carousel__slider {
        width: calc(100% - 96px);
      }

      .limits-carousel__no-scroll {
        flex-direction: column;
        width: 100%;
      }
    }

    &__subtitle {
      align-items: center;
      justify-content: center;
      font-weight: bold;
      gap: 8px;
      display: flex;
      font-size: 16px;
      padding: 22px 16px;

      i {
        color: map.get($colors, text) !important;

        .v-theme--dark & {
          color: rgb(var(--v-theme-on-surface)) !important;
        }
      }
    }

    &__controls {
      &.v-btn {
        border-radius: 8px;
        min-width: 40px;
        max-width: 40px;
        height: 68px;
      }

      i.v-icon {
        margin-right: 0 !important;
      }
    }
  }
</style>
